import { useState } from "react";
import { useQuery } from "react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

import { useSelector } from "react-redux";

import { getDashboardValorization, getDashboardValorizationGraph } from "@/api/Dashboard/dashboard";
import { formatMiles, formatMilesDecimalNew, validRoles, formatShortNumber } from "@/services/Utils";
import Fetching from "@/components/Fetching/Fetching";

import DashboardValorizationTable from "./DashboardValorizationTable";

import classes from "./dashboard-valorization.module.scss";

const DashboardValorization = ({ filters }) => {
	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const option = urlParams.get("option");

	const [period, setPeriod] = useState("year");
	const [showTable, setShowTable] = useState(option === "valorizacion");

	const { estate_parent_id, estate_id, type_currency } = useSelector((state) => state.auth.filter);

	const { data: responseData, isLoading } = useQuery(["dashboard_valorization", { ...filters, estate_parent_id, estate_id, type_currency }], () =>
		getDashboardValorization({ ...filters, estate_parent_id, estate_id, type_currency }),
	);

	const { data: responseGraph, isLoading: isLoadingGraph } = useQuery(
		["dashboard_valorization_graph", { ...filters, estate_parent_id, estate_id, type_currency, period }],
		() => getDashboardValorizationGraph({ ...filters, estate_parent_id, estate_id, type_currency, period }),
	);

	const value_percent_valued = responseData ? (responseData.valorizado / responseData.total) * 100 : 0;
	const percent_valued_number = value_percent_valued;
	const percent_valued = formatMilesDecimalNew(percent_valued_number.toString().replaceAll(".", ","));
	const percent_not_valued_number = responseData ? (responseData.sin_valorizar / responseData.total) * 100 : 0;
	const percent_not_valued = formatMilesDecimalNew(percent_not_valued_number.toString().replaceAll(".", ","));

	const modifiedGraph = responseGraph ? [...responseGraph] : [];
	if (modifiedGraph.length > 0) {
		modifiedGraph[modifiedGraph.length - 1].actual = responseData ? responseData.valorization : 0;
	}

	const handleShowTable = () => setShowTable(true);

	return (
		<>
			{isLoading ? (
				<Fetching topMargin={"5em"} bottomMargin={"5em"} />
			) : (
				<>
					<div className={classes.cardGroup}>
						<div className={classes.card}>
							<p className={classes.titleCard}>Productos en bodega</p>
							<p className={classes.valueCard}>{responseData ? formatMiles(responseData.quantity) : 0}</p>
							<p className={classes.subtext}>
								vs {formatMiles(responseData?.prev_quantity)} año anterior{" "}
								<span className={responseData?.percent_quantity < 0 ? classes.positive : classes.negative}>
									({responseData?.percent_quantity}%)
								</span>
							</p>
						</div>

						{isManager && (
							<div className={classes.card}>
								<p className={classes.titleCard}>Valorización actual</p>
								<p className={percent_valued_number === 100 ? classes.valueCardGood : classes.valueCardBad}>
									${responseData ? formatMilesDecimalNew(responseData.valorization.toString().replaceAll(".", ",")) : 0}
								</p>
								<p className={classes.subtext}>
									vs ${formatMilesDecimalNew((responseData?.prev_valorization ?? 0).toString().replaceAll(".", ","))} año anterior{" "}
									<span className={responseData?.percent_valorization < 0 ? classes.positive : classes.negative}>
										({responseData?.percent_valorization}%)
									</span>
								</p>
							</div>
						)}
					</div>

					{isManager && (
						<div className={classes.progressWrapper}>
							<div className={classes.progressBar}>
								<div className={classes.valued} style={{ width: `${value_percent_valued}%` }}></div>
							</div>
							<div className={classes.progressLabels}>
								<span>{formatMilesDecimalNew(percent_valued)}% valorizado histórico</span>
								<span className={classes.valuedButton} onClick={handleShowTable}>
									{formatMilesDecimalNew(percent_not_valued)}% sin valorizar
								</span>
							</div>
						</div>
					)}

					{isManager && (
						<>
							<div className={classes.graphHeader}>
								<h4 className={classes.graphTitle}>Comparación valorización</h4>
								<select value={period} onChange={(e) => setPeriod(e.target.value)} className={classes.periodSelector}>
									<option value="year">Año Anterior</option>
									<option value="semester">Semestre Anterior</option>
									<option value="trimester">Trimestre Anterior</option>
								</select>
							</div>

							{isLoadingGraph ? (
								<Fetching topMargin={"5em"} bottomMargin={"5em"} />
							) : (
								<ResponsiveContainer width="100%" height={250}>
									<BarChart data={modifiedGraph} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
										<XAxis dataKey="label" tick={{ fontSize: 12 }} />
										<YAxis tickFormatter={formatShortNumber} tick={{ fontSize: 12 }} />
										<CartesianGrid strokeDasharray="3 3" vertical={false} />
										<Tooltip formatter={(value) => `$${formatMilesDecimalNew(value.toString().replace(".", ","))}`} />
										<Legend />
										<Bar dataKey="previous" fill="#b39ddb" name="Periodo anterior" />
										<Bar dataKey="actual" fill="#17a2b8" name="Valorización Actual" />
									</BarChart>
								</ResponsiveContainer>
							)}

							{showTable && <DashboardValorizationTable filters={filters} showTable={showTable} setShowTable={setShowTable} />}
						</>
					)}
				</>
			)}
		</>
	);
};

export default DashboardValorization;
